import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CreateCustomChartModal } from '@/modules/admin-dashboard/components/dashboard/custom-charts/CreateCustomChartModal.tsx';
import CustomPieChart from '@/modules/admin-dashboard/components/dashboard/custom-charts/CustomPieChart.tsx';
import {
  DateRange,
  chartDateRangeOptions,
} from '@/modules/admin-dashboard/constants/chart-date-range-options.ts';
import {
  useDeleteCustomChartMutation,
  useGetCustomChartCalculationsQuery,
} from '@/modules/admin-dashboard/queries/custom-charts.ts';
import { ChartEmptyState, ConfirmModal, InfoCard, ModalProviderContext, SelectButtons } from 'ui';
import { EditIcon, TrashIcon } from 'ui/icons';
import { CustomChartResponse } from 'compliance-api';

dayjs.extend(quarterOfYear);

interface CustomChartProps {
  chart: CustomChartResponse;
}

const CustomChart = ({ chart }: CustomChartProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.All);

  const dateFilter = useMemo(() => {
    switch (dateRange) {
      case DateRange.CurrentQuarter:
        return {
          from_date: dayjs().startOf('quarter').format(),
          to_date: dayjs().endOf('quarter').format(),
        };
      case DateRange.LastQuarter:
        return {
          from_date: dayjs().subtract(1, 'quarter').startOf('quarter').format(),
          to_date: dayjs().subtract(1, 'quarter').endOf('quarter').format(),
        };
      case DateRange.LastYear:
        return {
          from_date: dayjs().subtract(1, 'year').startOf('year').format(),
          to_date: dayjs().subtract(1, 'year').endOf('year').format(),
        };
      default:
        return {};
    }
  }, [dateRange]);

  const { data: chartDataResponse, isFetching } = useGetCustomChartCalculationsQuery(
    { chart_id: chart.id, ...dateFilter },
    {
      enabled: !!chart.id,
    },
  );

  const { mutate: deleteChart, isLoading: isDeleting } = useDeleteCustomChartMutation();

  const handleEdit = useCallback(() => {
    openModal(CreateCustomChartModal, { chart });
  }, [openModal, chart]);

  const handleChartDelete = useCallback(() => {
    openModal(ConfirmModal, {
      onConfirm: async () => {
        deleteChart(chart.id);
      },
      title: <div>Are you sure you want to delete this custom chart?</div>,
    });
  }, [deleteChart, chart, openModal]);

  const chartData = useMemo(() => chartDataResponse?.data || [], [chartDataResponse]);

  const chartLabels = useMemo(() => chartData.map(item => item.group), [chartData]);
  const chartSeries = useMemo(() => chartData.map(item => item.metric_value), [chartData]);

  const selectedDateRangeOption = useMemo(
    () => chartDateRangeOptions.find(option => option.value === dateRange),
    [dateRange],
  );

  return (
    <InfoCard
      title={chart.name}
      loading={isFetching || isDeleting}
      headerAction={
        <div className="flex items-center gap-4">
          <button
            onClick={handleEdit}
            disabled={isDeleting}
            className="disabled:opacity-50 text-[#0369A1] hover:text-sky-800"
            aria-label="Edit Chart"
          >
            <img src={EditIcon} alt="Edit" className="w-5 h-5" />
          </button>
          <button
            onClick={handleChartDelete}
            disabled={isDeleting}
            className="disabled:opacity-50 text-[#0369A1] hover:text-sky-800"
            aria-label="Delete Chart"
          >
            <img src={TrashIcon} alt="Delete" className="w-5 h-5" />
          </button>
        </div>
      }
    >
      <div className="w-full flex justify-end">
        <SelectButtons
          options={chartDateRangeOptions}
          selected={selectedDateRangeOption}
          onChange={setDateRange}
        />
      </div>
      <div className="w-full mt-4 h-full">
        {isFetching && (
          <div className="flex justify-center items-center font-medium text-lg h-full">
            <ChartEmptyState isLoading={isFetching} />
          </div>
        )}
        {!isFetching && !chartData.length && (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-lg font-semibold text-center">No data to display for this chart</p>
          </div>
        )}
        {!isFetching && !!chartData.length && (
          <>
            {chart.chart_type === 'pie' && (
              <CustomPieChart
                series={chartSeries}
                labels={chartLabels}
                metricName={chartDataResponse?.metric_field}
              />
            )}
            {chart.chart_type !== 'pie' && (
              <div className="w-full h-full flex items-center justify-center">
                <pre className="text-xs">
                  Chart Type: {chart.chart_type}
                  <br />
                  Data: {JSON.stringify(chartData, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </InfoCard>
  );
};

export default CustomChart;
